import type { AnnualLicenseConfig } from '@/mocks/superAdminPaymentProcessors';

interface Props {
  config: AnnualLicenseConfig;
  editable?: boolean;
  onEdit?: () => void;
}

export default function AnnualLicenseConfigCard({ config, editable, onEdit }: Props) {
  const projected = config.stats.payingRadios * config.amount;
  const formattedProjected = projected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${config.enabled ? 'bg-emerald-50' : 'bg-foreground-100'}`}>
            <i className={`${config.enabled ? 'ri-shield-check-line text-emerald-600' : 'ri-shield-cross-line text-foreground-400'} text-lg`}></i>
          </div>
          <div className="min-w-0">
            <h3 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
              Licencia Anual Plataforma
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap ${config.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-foreground-200 text-foreground-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${config.enabled ? 'bg-emerald-500' : 'bg-foreground-400'}`}></span>
                {config.enabled ? 'Activa' : 'Desactivada'}
              </span>
            </h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">Configuración global · Periodicidad <strong className="text-foreground-600">Anual</strong></p>
          </div>
        </div>
        {editable && (
          <button
            onClick={onEdit}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-pencil-line text-[11px]"></i>Editar
          </button>
        )}
      </div>

      <div className="p-5">
        {/* Amount + Description */}
        <div className="flex flex-col sm:flex-row gap-5 mb-5">
          {/* Amount */}
          <div className="bg-background-100 rounded-xl px-4 py-3 flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-lg bg-background-50 flex items-center justify-center shrink-0">
              <i className="ri-money-dollar-circle-line text-foreground-500 text-base"></i>
            </div>
            <div>
              <p className="text-foreground-400 text-[9px] font-medium">Monto</p>
              <p className="text-foreground-950 text-xl font-bold">${config.amount.toFixed(2)}</p>
            </div>
          </div>

          {/* Description */}
          <div className="flex-1 min-w-0">
            <p className="text-foreground-400 text-[9px] font-medium mb-1">Descripción</p>
            <p className="text-foreground-700 text-xs leading-relaxed">{config.description}</p>
          </div>
        </div>

        {/* Includes + Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Includes */}
          <div>
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-3">Incluye</p>
            <ul className="space-y-1.5">
              {config.includes.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-foreground-700 text-xs">
                  <i className="ri-check-line text-emerald-500 text-xs shrink-0"></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Stats */}
          <div>
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-3">Estadísticas</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px]">Radios que la pagan</p>
                <p className="text-foreground-800 text-lg font-bold mt-0.5">{config.stats.payingRadios.toLocaleString()}</p>
              </div>
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px]">Vencen este mes</p>
                <p className="text-foreground-800 text-lg font-bold mt-0.5">{config.stats.expiringThisMonth.toLocaleString()}</p>
              </div>
              <div className="col-span-2 bg-accent-100/70 rounded-xl p-3 border border-accent-200/50">
                <p className="text-accent-700 text-[9px] font-medium">Ingresos proyectados</p>
                <p className="text-accent-800 text-lg font-bold mt-0.5">${formattedProjected}</p>
                <p className="text-accent-600 text-[9px] mt-0.5">{config.stats.payingRadios.toLocaleString()} radios × ${config.amount.toFixed(2)} = ${formattedProjected}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}